import type { MeResponse } from "@safeplate/contracts";
import { Button, Card, CardContent, Input, Label } from "@safeplate/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useRef, useState, type FormEvent, type ReactNode } from "react";

import { createOrganisation } from "../api/client.js";
import { signOut } from "../api/auth.js";
import { displayError } from "../api/errors.js";
import { Brand } from "../components/brand.js";
import { useDocumentTitle } from "../components/document-title.js";
import { invitationTokenFromInput } from "../auth/invitations.js";
import { createLocation } from "../setup/setup-api.js";
import { onboardingRequests, type OnboardingForm } from "./onboarding-model.js";

function Shell({ children, email }: { children: ReactNode; email: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return (
    <main className="min-h-dvh bg-canvas px-4 py-8 sm:px-8 sm:py-12">
      <div className="mx-auto max-w-xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Brand />
          <div className="flex items-center gap-3 text-sm text-stone-500">
            <span className="truncate">{email}</span>
            <button
              className="font-semibold text-emerald-800 underline-offset-4 hover:underline"
              onClick={() => {
                void signOut()
                  .catch(() => undefined)
                  .then(async () => {
                    queryClient.clear();
                    await navigate({ to: "/sign-in" });
                  });
              }}
              type="button"
            >
              Sign out
            </button>
          </div>
        </div>
        <div className="mt-10">{children}</div>
      </div>
    </main>
  );
}

function Field({
  autoComplete,
  id,
  label,
  onChange,
  required,
  value,
}: {
  autoComplete?: string;
  id: string;
  label: string;
  onChange: (value: string) => void;
  required?: boolean;
  value: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        autoComplete={autoComplete}
        id={id}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        value={value}
      />
    </div>
  );
}

function BusinessForm({
  email,
  onCreated,
  onInvited,
}: {
  email: string;
  onCreated: () => Promise<void>;
  onInvited: () => void;
}) {
  const [form, setForm] = useState<OnboardingForm>({
    city: "",
    legalName: "",
    locationName: null,
    tradingName: "",
  });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useDocumentTitle("Set up your business");
  const keys = useRef({
    location: globalThis.crypto.randomUUID(),
    organisation: globalThis.crypto.randomUUID(),
  });
  const set = (key: keyof OnboardingForm) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);
    const requests = onboardingRequests(form);
    try {
      // Both keys stay fixed for this form, so a retry after a failure
      // returns the same business and location instead of making new ones.
      const organisation = await createOrganisation(
        requests.organisation,
        keys.current.organisation,
      );
      await createLocation(requests.location, {
        idempotencyKey: keys.current.location,
        orgId: organisation.id,
      });
      await onCreated();
    } catch (caught) {
      setError(displayError(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <Shell email={email}>
      <h1 className="text-3xl font-semibold tracking-tight text-stone-950">
        Set up your business
      </h1>
      <Card className="mt-6">
        <CardContent className="p-5 sm:p-7">
          <form className="space-y-5" onSubmit={(event) => void submit(event)}>
            <Field
              autoComplete="organization"
              id="legal-name"
              label="Legal name"
              onChange={set("legalName")}
              required
              value={form.legalName}
            />
            <Field
              id="trading-name"
              label="Trading name (optional)"
              onChange={set("tradingName")}
              value={form.tradingName}
            />
            <div className="grid gap-5 border-t border-stone-200 pt-5 sm:grid-cols-2">
              <Field
                id="location-name"
                label="First location"
                onChange={set("locationName")}
                required
                value={
                  form.locationName ?? onboardingRequests(form).location.name
                }
              />
              <Field
                autoComplete="address-level2"
                id="location-city"
                label="City"
                onChange={set("city")}
                value={form.city}
              />
            </div>
            {error ? (
              <p
                className="rounded-md border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-800"
                role="alert"
              >
                {error}
              </p>
            ) : null}
            <Button className="w-full" disabled={pending} type="submit">
              {pending ? "Creating…" : "Create business"}
            </Button>
          </form>
        </CardContent>
      </Card>
      <p className="mt-6 text-center text-sm text-stone-600">
        <button
          className="font-semibold text-emerald-800 underline-offset-4 hover:underline"
          onClick={onInvited}
          type="button"
        >
          I’ve been invited
        </button>
      </p>
    </Shell>
  );
}

export function OrganisationOnboarding({
  me,
  onCreated,
}: {
  me: MeResponse;
  onCreated: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState<"create" | "join">("create");
  const [invitationInput, setInvitationInput] = useState("");
  const [invitationError, setInvitationError] = useState<string | null>(null);

  if (step === "create") {
    return (
      <BusinessForm
        email={me.user.email}
        onCreated={onCreated}
        onInvited={() => setStep("join")}
      />
    );
  }

  function join(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const token = invitationTokenFromInput(invitationInput);
    if (!token) {
      setInvitationError("Paste the full link from your invitation email.");
      return;
    }
    void navigate({ search: { token }, to: "/invitations/accept" });
  }

  return (
    <Shell email={me.user.email}>
      <Button
        className="mb-4 -ml-2"
        onClick={() => setStep("create")}
        size="sm"
        type="button"
        variant="ghost"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Back
      </Button>
      <h1 className="text-3xl font-semibold tracking-tight text-stone-950">
        I’ve been invited
      </h1>
      <Card className="mt-6">
        <CardContent className="p-5 sm:p-7">
          <form className="space-y-4" onSubmit={join}>
            <Field
              id="workplace-invitation"
              label="Invitation link"
              onChange={setInvitationInput}
              required
              value={invitationInput}
            />
            {invitationError ? (
              <p className="text-sm font-semibold text-red-800" role="alert">
                {invitationError}
              </p>
            ) : null}
            <Button className="w-full" type="submit">
              Continue
            </Button>
          </form>
        </CardContent>
      </Card>
    </Shell>
  );
}
